import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ArrowBack from '@mui/icons-material/ArrowBack';
import ArrowForward from '@mui/icons-material/ArrowForward';
import ZoomIn from '@mui/icons-material/ZoomIn';
import ImageViewer from 'react-simple-image-viewer';
import FlexBox from './FlexBox';
import BazarAvatar from './BazarAvatar';
import { styled } from '@mui/material/styles';

// Styled components for better UX
const ImageContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '480px',
  backgroundColor: theme.palette.background.paper,
  borderRadius: 2,
  overflow: 'hidden'
}));

const NavigationArrow = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  backgroundColor: 'rgba(255, 255, 255, 0.3)',
  color: theme.palette.text.primary,
  opacity: 1,
  transition: 'all 0.3s ease',
  zIndex: 2,
  width: '32px',
  height: '32px',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.6)',
    transform: 'translateY(-50%) scale(1.05)'
  },
  '& .MuiSvgIcon-root': {
    fontSize: '16px'
  },
  '&.left': {
    left: theme.spacing(1)
  },
  '&.right': {
    right: theme.spacing(1)
  }
}));

const ZoomButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1),
  right: theme.spacing(1),
  backgroundColor: 'rgba(255, 255, 255, 0.3)',
  color: theme.palette.text.primary,
  opacity: 1,
  transition: 'all 0.3s ease',
  zIndex: 2,
  width: '32px',
  height: '32px',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.6)',
    transform: 'scale(1.05)'
  },
  '& .MuiSvgIcon-root': {
    fontSize: '16px'
  }
}));

const ImageClickArea = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  cursor: 'pointer',
  zIndex: 1
});

const MainImage = styled('img')(() => ({
  maxWidth: '100%',
  maxHeight: '440px',
  objectFit: 'contain',
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'scale(1.02)'
  }
}));

const ThumbnailContainer = styled(FlexBox)(({ theme }) => ({
  gap: theme.spacing(1),
  padding: theme.spacing(1),
  overflowX: 'auto',
  '&::-webkit-scrollbar': {
    height: '4px'
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '2px'
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#c1c1c1',
    borderRadius: '2px'
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#a8a8a8'
  }
}));

const ThumbnailBox = styled(Box)(({ theme, selected }) => ({
  height: 70,
  width: 60,
  borderRadius: 2,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  border: `2px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  flexShrink: 0,
  '&:hover': {
    borderColor: theme.palette.primary.main,
    transform: 'scale(1.05)'
  }
}));

export default function ProductViewGridItemImage({ product }) {
  const imgGroup = product?.images?.map((a) => a.url) || [];

  const [selectedImage, setSelectedImage] = useState(0);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Swipe functionality
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [viewerTouchStart, setViewerTouchStart] = useState(null);
  const [viewerTouchEnd, setViewerTouchEnd] = useState(null);

  // Minimum swipe distance
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      moveRight();
    }
    if (isRightSwipe) {
      moveLeft();
    }
  };

  const onViewerTouchStart = (e) => {
    setViewerTouchEnd(null);
    setViewerTouchStart(e.targetTouches[0].clientX);
  };

  const onViewerTouchMove = (e) => {
    setViewerTouchEnd(e.targetTouches[0].clientX);
  };

  const onViewerTouchEnd = () => {
    if (!viewerTouchStart || !viewerTouchEnd) return;

    const distance = viewerTouchStart - viewerTouchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      moveRight();
    }
    if (isRightSwipe) {
      moveLeft();
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (isViewerOpen) {
        switch (event.key) {
          case 'Escape':
            closeImageViewer();
            break;
          case 'ArrowLeft':
            event.preventDefault();
            moveLeft();
            break;
          case 'ArrowRight':
            event.preventDefault();
            moveRight();
            break;
          default:
            break;
        }
      } else {
        switch (event.key) {
          case 'ArrowLeft':
            event.preventDefault();
            moveLeft();
            break;
          case 'ArrowRight':
            event.preventDefault();
            moveRight();
            break;
          default:
            break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage, isViewerOpen, imgGroup.length]);

  const handleImageClick = (ind) => () => {
    setSelectedImage(ind);
  };

  const openImageViewer = useCallback((index) => {
    setCurrentImage(index);
    setIsViewerOpen(true);
  }, []);

  const closeImageViewer = () => {
    setCurrentImage(0);
    setIsViewerOpen(false);
  };

  const moveRight = () => {
    if (imgGroup.length === 0) return;
    const nextImageIdx = selectedImage + 1;
    if (imgGroup.length > nextImageIdx) {
      setSelectedImage(nextImageIdx);
    } else {
      setSelectedImage(0);
    }
  };

  const moveLeft = () => {
    if (imgGroup.length === 0) return;
    const nextImageIdx = selectedImage - 1;
    if (nextImageIdx >= 0) {
      setSelectedImage(nextImageIdx);
    } else {
      setSelectedImage(imgGroup.length - 1);
    }
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleImageError = () => {
    setImageLoaded(true);
    // Could add error state handling here
  };

  const preventContextMenu = (e) => {
    e.preventDefault();
  };

  if (!imgGroup || imgGroup.length === 0) {
    return (
      <Grid item sm={4} xs={12} alignItems="center" style={{ minHeight: '580px' }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="480px"
          bgcolor="grey.100"
          borderRadius={1}>
          <Box textAlign="center" color="text.primary">
            No images available
          </Box>
        </Box>
      </Grid>
    );
  }

  return (
    <Grid item sm={4} xs={12} alignItems="center" style={{ minHeight: '580px' }}>
      <Box>
        <ImageContainer
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onContextMenu={preventContextMenu}>
          {/* Navigation Arrows */}
          {imgGroup.length > 1 && (
            <>
              <NavigationArrow
                className="navigation-arrow left"
                onClick={moveLeft}
                aria-label="Previous image"
                size="large">
                <ArrowBack />
              </NavigationArrow>
              <NavigationArrow
                className="navigation-arrow right"
                onClick={moveRight}
                aria-label="Next image"
                size="large">
                <ArrowForward />
              </NavigationArrow>
            </>
          )}

          {/* Zoom Button */}
          <ZoomButton
            className="zoom-button"
            onClick={() => openImageViewer(selectedImage)}
            aria-label="Open image viewer"
            size="large">
            <ZoomIn />
          </ZoomButton>

          {/* Main Image */}
          <MainImage
            src={imgGroup[selectedImage]}
            alt={product?.name_bg || `Product image ${selectedImage + 1}`}
            onLoad={handleImageLoad}
            onError={handleImageError}
            onContextMenu={preventContextMenu}
            style={{ opacity: imageLoaded ? 1 : 0 }}
            draggable={false}
          />

          {/* Clickable area for zoom */}
          <ImageClickArea
            onClick={() => openImageViewer(selectedImage)}
            onContextMenu={preventContextMenu}
            aria-label="Click to zoom image"
          />

          {/* Full Screen Viewer */}
          {isViewerOpen && (
            <div
              onTouchStart={onViewerTouchStart}
              onTouchMove={onViewerTouchMove}
              onTouchEnd={onViewerTouchEnd}
              onContextMenu={preventContextMenu}>
              <ImageViewer
                src={imgGroup}
                currentIndex={currentImage}
                onClose={closeImageViewer}
                backgroundStyle={{
                  backgroundColor: 'rgba(0,0,0,0.95)',
                  zIndex: 9999
                }}
                closeOnClickOutside={true}
                closeOnEscape={true}
              />
            </div>
          )}
        </ImageContainer>

        {/* Thumbnails */}
        {imgGroup.length > 1 && (
          <ThumbnailContainer mt={2}>
            {imgGroup.map((url, ind) => (
              <ThumbnailBox
                key={ind}
                selected={selectedImage === ind}
                onClick={handleImageClick(ind)}
                onContextMenu={preventContextMenu}
                role="button"
                tabIndex={0}
                aria-label={`View image ${ind + 1}`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleImageClick(ind)();
                  }
                }}>
                <BazarAvatar
                  src={url}
                  variant="square"
                  height={66}
                  width={56}
                  onContextMenu={preventContextMenu}
                  alt={`${product?.name_bg || 'Product'} thumbnail ${ind + 1}`}
                />
              </ThumbnailBox>
            ))}
          </ThumbnailContainer>
        )}
      </Box>
    </Grid>
  );
}

ProductViewGridItemImage.propTypes = {
  product: PropTypes.shape({
    name_bg: PropTypes.string,
    images: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string.isRequired
      })
    )
  })
};
